import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { Logo } from "../components/Logo.tsx";
import { Banner, Loading } from "../components/Status.tsx";
import { ApiFailure, steamSignInUrl } from "../lib/api.ts";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useAcceptInvite, useInvitePreview, useMe } from "../lib/queries.ts";

/** Opened from an invite link, signed in or not. */
export function JoinPage() {
  const { t } = useTranslation();
  const { code = "" } = useParams();
  const me = useMe();
  const preview = useInvitePreview(code);
  const accept = useAcceptInvite();
  const { select } = useCurrentTeam();
  const navigate = useNavigate();

  let body: React.ReactNode;
  if (preview.isPending || me.isPending) {
    body = <Loading />;
  } else if (preview.error) {
    const err = preview.error;
    const gone = err instanceof ApiFailure && err.code === "invite_expired";
    body = (
      <>
        <Banner tone="warning">{gone ? t("join.expired") : t("join.notFound")}</Banner>
        <Link className="btn btn-secondary" to="/">
          {t("common.home")}
        </Link>
      </>
    );
  } else {
    const p = preview.data;
    body = (
      <>
        <p className="eyebrow">{t("join.eyebrow")}</p>
        <h1 className="page-title">{t("join.title", { team: p.teamName })}</h1>
        <p className="muted">{t("join.members", { count: p.memberCount })}</p>
        {p.alreadyMember ? (
          <>
            <Banner tone="info">{t("join.already", { team: p.teamName })}</Banner>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                select(p.teamId);
                navigate("/team");
              }}
            >
              {t("join.open")}
            </button>
          </>
        ) : p.full ? (
          <Banner tone="warning">{t("join.full", { team: p.teamName })}</Banner>
        ) : !me.data?.user ? (
          <a className="btn btn-primary btn-large" href={steamSignInUrl(`/join/${code}`)}>
            {t("join.signInToJoin")}
          </a>
        ) : (
          <>
            {accept.error && <Banner tone="warning">{errorText(t, accept.error)}</Banner>}
            <button
              type="button"
              className="btn btn-primary btn-large"
              disabled={accept.isPending}
              onClick={() =>
                accept.mutate(code, {
                  onSuccess: (team) => {
                    select(team.id);
                    navigate("/team", { replace: true, state: { joined: team.name } });
                  },
                })
              }
            >
              {t("join.accept", { team: p.teamName })}
            </button>
          </>
        )}
      </>
    );
  }

  return (
    <div className="page page-narrow join">
      <Logo />
      <div className="card join-card">{body}</div>
    </div>
  );
}
